import React, { useContext, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { TableRow } from '@material-ui/core';
import { UserContext } from '@/components/App';
import { UPDATE_CLIENT_BY_PK } from '@/graphql/mutations/clients';
import { isValidEmail } from '@/utilities/validateEmail';
import { Skipped } from '@/components/CorporateUsers/shippers/blocks';
import ExternalIdentifiers from '@/components/CorporateUsers/modals/AccountDetailsModal/ExternalIdentifiers';
import { Card, ContactEditModal, DataCell, DataTable, EditCell, Header, Section, StrongCell } from '../blocks';
import { EMPTY_VALUE } from '../constants';
import { contactRowsFor } from '../utils';
import EditFieldsModal from '../modals/EditFieldsModal';

const BILLING_FIELDS = [
    { key: 'billing_email', label: 'Billing Email' },
    { key: 'billing_address', label: 'Street Address' },
    { key: 'billing_city', label: 'City' },
    { key: 'billing_state', label: 'State' },
    { key: 'billing_zip', label: 'Zip Code' },
    { key: 'summarized_billing', label: 'Summarized Billing', type: 'boolean' },
];

// Billing.js is the shipper's own editor for these columns; like it, an emptied field saves as ''.
const billingDraftFor = (client) =>
    BILLING_FIELDS.reduce((acc, { key, type }) => {
        const value = type === 'boolean' ? String(!!client?.[key]) : client?.[key] || '';
        return { ...acc, [key]: value };
    }, {});

const billingPatchFor = (draft) =>
    BILLING_FIELDS.reduce((acc, { key, type }) => {
        const value = type === 'boolean' ? draft[key] === 'true' : draft[key] ?? '';
        return { ...acc, [key]: value };
    }, {});

const CompanyInfo = () => {
    const { client } = useContext(UserContext);
    const [editing, setEditing] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [updateClient, { loading: saving }] = useMutation(UPDATE_CLIENT_BY_PK);

    const contacts = useMemo(() => contactRowsFor(client), [client]);
    // `stripe_payment_method` is a Stripe id; brand and last4 live only in Stripe.
    const cardOnFile = client?.stripe_payment_method ? 'Card on file' : EMPTY_VALUE;

    // On failure the modal stays open with the draft intact and says so.
    const save = async (update) => {
        setSaveError(null);
        try {
            await updateClient({ variables: { client_id: client.client_id, update } });
            setEditing(null);
        } catch (err) {
            Sentry.captureException(err);
            setSaveError("Couldn't save. Try again.");
        }
    };

    const closeEditor = () => {
        setSaveError(null);
        setEditing(null);
    };

    const saveBilling = (draft) => save(billingPatchFor(draft));

    const saveContact = (update) => {
        const email = update[editing.slot.emailField];
        if (email && !isValidEmail(email)) {
            setSaveError('Enter a valid email.');
            return;
        }

        save(update);
    };

    return (
        <>
            {/* "Client identity" goes here once its fields have columns on `clients`. */}

            <Section>
                <Header title="Billing information" />
                <DataTable
                    columns={['Name', 'Billing Code', 'Email', 'Terms', 'Cadence', 'Summarized billing', 'Card info']}
                >
                    <TableRow>
                        <DataCell>
                            <StrongCell>{client?.business_name || EMPTY_VALUE}</StrongCell>
                        </DataCell>
                        <DataCell>
                            <Skipped />
                        </DataCell>
                        <DataCell>{client?.billing_email || EMPTY_VALUE}</DataCell>
                        <DataCell>
                            <Skipped />
                        </DataCell>
                        <DataCell>
                            <Skipped />
                        </DataCell>
                        <DataCell>{client?.summarized_billing ? 'Yes' : 'No'}</DataCell>
                        <DataCell>{cardOnFile}</DataCell>
                        <EditCell label="Edit billing information" onClick={() => setEditing({ kind: 'billing' })} />
                    </TableRow>
                </DataTable>
            </Section>

            <Section>
                <Header title="Identifier Details" note="· your organization's ids for this shipper" />
                <Card>
                    <div
                        css={css`
                            padding: 20px;
                        `}
                    >
                        <ExternalIdentifiers clientId={client?.client_id} promptWhenEmpty />
                    </div>
                </Card>
            </Section>

            <Section>
                <Header title="Key contacts" note="· mirrored" />
                {/* No "Add contact": `clients` has exactly these three slots. */}
                <DataTable columns={['Name', 'Title', 'Email', 'Phone']}>
                    {contacts.map((contact) => (
                        <TableRow key={contact.key}>
                            <DataCell>
                                <StrongCell>{contact.name || EMPTY_VALUE}</StrongCell>
                            </DataCell>
                            <DataCell>{contact.title}</DataCell>
                            <DataCell>{contact.email || EMPTY_VALUE}</DataCell>
                            <DataCell>{contact.phone || EMPTY_VALUE}</DataCell>
                            <EditCell
                                label={`Edit ${contact.title}`}
                                onClick={() => setEditing({ kind: 'contact', slot: contact })}
                            />
                        </TableRow>
                    ))}
                </DataTable>
            </Section>

            <EditFieldsModal
                open={editing?.kind === 'billing'}
                title="Edit billing information"
                fields={BILLING_FIELDS}
                values={billingDraftFor(client)}
                saving={saving}
                error={saveError}
                onClose={closeEditor}
                onSave={saveBilling}
            />

            <ContactEditModal
                open={editing?.kind === 'contact'}
                slot={editing?.slot}
                saving={saving}
                error={saveError}
                onClose={closeEditor}
                onSave={saveContact}
            />
        </>
    );
};

export default CompanyInfo;
